import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  ExternalLink, 
  ArrowRight, 
  ArrowUp, 
  Clock, 
  ShieldCheck, 
  Building2
} from 'lucide-react';
import logo from '../assets/logo.png';
import { services } from '../data/services';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t, getService } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-[#0A1424] text-white pt-16 pb-8 overflow-hidden border-t border-teal/20">
      {/* Ambient Lighting Background Accents */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[300px] bg-clinical/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-[400px] h-[300px] bg-teal/10 rounded-full blur-[100px] pointer-events-none"></div>
      
      {/* Delicate Gradient Top Accent Bar */}
      <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-teal to-transparent opacity-75"></div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 pb-14 border-b border-white/10">
          
          {/* Brand & Mission Column (Col 1 to 4) */}
          <div className="lg:col-span-4 space-y-6">
            <Link 
              to="/" 
              className="inline-block bg-white p-3.5 rounded-2xl shadow-xl shadow-black/30 border border-white/30 hover:scale-[1.02] transition-transform duration-300 group"
            >
              <img 
                src={logo} 
                alt="Sree Tekke Eye Clinic" 
                className="h-11 w-auto object-contain" 
              />
            </Link>

            <p className="text-gray-300 text-sm leading-relaxed max-w-sm">
              {t('footer.brandDesc')}
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2 pt-1">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-300 font-medium">
                <ShieldCheck size={14} className="text-teal" />
                <span>{t('footer.nabhCare')}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-gray-300 font-medium">
                <Building2 size={14} className="text-clinical" />
                <span>{t('footer.modernCenters')}</span>
              </span>
            </div>

            {/* Social Media Links & Language Switcher */}
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">{t('footer.connectWithUs')}</p>
              <div className="flex items-center space-x-3 mb-5">
                <a 
                  href="https://www.instagram.com/sreetekkeeyeclinicsangli?exln=MWtncDhhcXJmNWx3Yw%3D%3D" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Instagram" 
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] hover:border-transparent transition-all duration-300 hover:scale-110 shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                </a>
                <a 
                  href="https://maps.app.goo.gl/fnjzcYXjEd8KLXGh8" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Google Maps Location" 
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:bg-clinical hover:border-clinical transition-all duration-300 hover:scale-110 shadow-sm"
                >
                  <MapPin size={18} />
                </a>
                <a 
                  href="/" 
                  aria-label="Facebook" 
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] transition-all duration-300 hover:scale-110 shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links Column (Col 5 to 6) */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-bold uppercase tracking-wider text-teal mb-5 flex items-center gap-2">
              <span className="w-1.5 h-3.5 bg-teal rounded-full"></span>
              <span>{t('footer.quickLinks')}</span>
            </h4>
            <ul className="space-y-3">
              {[
                { name: t('nav.home'), path: '/' },
                { name: t('nav.about'), path: '/about' },
                { name: t('nav.services'), path: '/services' },
                { name: t('nav.doctors'), path: '/doctors' },
                { name: t('nav.branches'), path: '/branches' },
                { name: t('nav.gallery'), path: '/gallery' },
                { name: t('nav.contact'), path: '/contact' }
              ].map((link, idx) => (
                <li key={idx}>
                  <Link 
                    to={link.path} 
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <ArrowRight size={13} className="text-teal/50 mr-2 group-hover:translate-x-1 group-hover:text-teal transition-all" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Services Column (Col 7 to 9) */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-bold uppercase tracking-wider text-teal mb-5 flex items-center gap-2">
              <span className="w-1.5 h-3.5 bg-teal rounded-full"></span>
              <span>{t('footer.popularServices')}</span>
            </h4>
            <ul className="space-y-2.5">
              {services.slice(0, 6).map((service) => {
                const s = getService(service);
                return (
                  <li key={service.id}>
                    <Link 
                      to="/services" 
                      className="text-gray-300 hover:text-white transition-colors text-sm flex items-start group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-teal/70 mr-2.5 mt-2 shrink-0 group-hover:bg-teal group-hover:scale-125 transition-all"></span>
                      <span className="leading-snug">{s.name}</span>
                    </Link>
                  </li>
                );
              })}
              <li className="pt-2">
                <Link 
                  to="/services" 
                  className="inline-flex items-center gap-1.5 text-teal hover:text-teal/80 font-semibold text-xs tracking-wide group"
                >
                  <span>{t('servicesSection.viewAll')}</span>
                  <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Main Clinic Card (Col 10 to 12) */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-sm font-bold uppercase tracking-wider text-teal mb-5 flex items-center gap-2">
              <span className="w-1.5 h-3.5 bg-teal rounded-full"></span>
              <span>{t('contactSection.mainClinic')}</span>
            </h4>

            {/* Interactive Sangli Location Card */}
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md hover:border-teal/50 transition-all duration-300 group">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-teal/15 text-teal flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-teal group-hover:text-white transition-colors">
                  <MapPin size={18} />
                </div>
                <div>
                  <h5 className="font-bold text-white text-sm">{t('contactSection.mainClinicTitle')}</h5>
                  <p className="text-gray-300 text-xs mt-0.5">{t('contactSection.mainClinicLocation')}</p>
                  
                  <a 
                    href="https://maps.app.goo.gl/fnjzcYXjEd8KLXGh8" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center gap-1.5 text-teal hover:text-white text-xs font-semibold mt-2.5 px-2.5 py-1 rounded-md bg-teal/10 hover:bg-teal/30 border border-teal/30 transition-all"
                  >
                    <span>{t('contactSection.openInMaps')}</span>
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Contact Info */}
            <div className="space-y-2.5 pt-1">
              <a 
                href="tel:+919876543210" 
                className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 text-gray-300 hover:text-white text-sm transition-all"
              >
                <div className="w-7 h-7 rounded-lg bg-clinical/20 text-clinical flex items-center justify-center shrink-0">
                  <Phone size={14} />
                </div>
                <span className="font-medium text-xs sm:text-sm">+91 98765 43210</span>
              </a>

              <a 
                href="mailto:info@sreetekke.com" 
                className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 text-gray-300 hover:text-white text-sm transition-all"
              >
                <div className="w-7 h-7 rounded-lg bg-teal/20 text-teal flex items-center justify-center shrink-0">
                  <Mail size={14} />
                </div>
                <span className="font-medium text-xs sm:text-sm">info@sreetekke.com</span>
              </a>

              <div className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 border border-white/5 text-gray-300 text-xs">
                <div className="w-7 h-7 rounded-lg bg-accent/20 text-accent flex items-center justify-center shrink-0">
                  <Clock size={14} />
                </div>
                <div>
                  <span className="text-gray-400 block text-[11px]">{t('footer.dailyOpd')}</span>
                  <span className="font-semibold text-white">{t('footer.dailyOpdHours')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar: Copyright & Back-to-Top */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={scrollToTop}
              aria-label="Back to Top"
              title="Back to Top"
              className="w-8 h-8 rounded-full bg-white/5 hover:bg-teal hover:text-white border border-white/10 text-gray-300 transition-all flex items-center justify-center group active:scale-95 shrink-0 shadow-xs"
            >
              <ArrowUp size={15} className="group-hover:-translate-y-0.5 transition-transform text-teal group-hover:text-white" />
            </button>
            <p className="text-gray-400 text-xs text-center md:text-left">
              &copy; {new Date().getFullYear()} <span className="text-white font-medium">{t('hero.society')}</span>. {t('footer.allRightsReserved')}
            </p>
          </div>

          <div className="flex items-center gap-6 text-xs text-gray-400 md:mr-32 lg:mr-44">
            <Link to="/contact" className="hover:text-teal transition-colors">{t('nav.contact')}</Link>
            <span className="text-gray-700">•</span>
            <Link to="/branches" className="hover:text-teal transition-colors">{t('branchesSection.badge')}</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
